import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRight, FileText, Briefcase, Compass, Users, Palette, Award, Brain, Wrench, Heart } from 'lucide-react';
import { ProjectCard } from '../components/ProjectCard';
import { LogoCarousel } from '../components/LogoCarousel';
import { ServiceFlipCard } from '../components/ServiceFlipCard';
import { Footer } from '../components/Footer';
import { Testimonials } from '../components/Testimonials';
type AboutTab = 'love' | 'values' | 'bio';
export function Home() {
  const [activeTab, setActiveTab] = useState<AboutTab>('love');
  return <div className="w-full" data-id="element-517">
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center pt-20 overflow-hidden" data-id="element-518">
        {/* Background Image */}
        <div className="absolute inset-0 bg-cover bg-center" style={{
        backgroundImage: 'url(https://cdn.magicpatterns.com/uploads/iFyHfy639aqnsGBW1v5NYD/bluesky.png)'
      }} data-id="element-519" />
        {/* Optional overlay for better text readability */}
        <div className="absolute inset-0 bg-blue-900/10" data-id="element-520" />

        <div className="max-w-7xl mx-auto px-6 relative z-10 w-full" data-id="element-521">
          <div className="max-w-4xl mx-auto flex flex-col items-center text-center" data-id="element-522">
            <img src="https://cdn.magicpatterns.com/uploads/uCN62GfprPhBw8BbwTjjW2/25-09-08_BSW_Headshots_ES-0001_233.jpg" alt="Rebecca" className="w-40 h-40 rounded-full object-cover border-4 border-white/20 shadow-xl mb-8" data-id="element-523" />

            <h1 className="text-5xl md:text-7xl font-thin text-white leading-tight mb-8" data-id="element-524">
              Hi, I'm Rebecca
            </h1>

            <p className="text-xl md:text-2xl text-blue-50 font-light max-w-2xl mb-12 leading-relaxed" data-id="element-525">
              I believe that technology, made with caring hands, can make a
              positive difference in the lives of all.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center" data-id="element-526">
              <button onClick={() => document.getElementById('work')?.scrollIntoView({
              behavior: 'smooth'
            })} className="px-8 py-4 bg-white text-blue-600 rounded-full font-medium hover:bg-blue-50 transition-colors text-center shadow-lg shadow-blue-900/10" data-id="element-527">
                View My Work
              </button>
              <a href="https://drive.google.com/file/d/1JkohzcmpUul7p0pN-sH1hINJczIXNqNs/view?usp=sharing" target="_blank" rel="noopener noreferrer" className="px-8 py-4 bg-transparent border border-white text-white rounded-full font-medium hover:bg-white/10 transition-colors text-center inline-flex items-center justify-center gap-2" data-id="element-528">
                <FileText size={18} data-id="element-529" />
                Download Resume
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Logo Carousel Section */}
      <section className="py-24 bg-slate-50 overflow-hidden" data-id="element-530">
        <div className="max-w-7xl mx-auto px-6 mb-16" data-id="element-531">
          <div className="text-center max-w-3xl mx-auto" data-id="element-532">
            <h2 className="text-3xl md:text-4xl font-light text-slate-900 mb-6" data-id="element-533">
              I create thoughtful digital work
            </h2>
            <p className="text-lg text-slate-600 font-light leading-relaxed" data-id="element-534">
              The products I've worked on have genuinely helped people, whether
              in their health, their jobs, or their everyday lives.
            </p>
          </div>
        </div>
        <LogoCarousel data-id="element-535" />
      </section>

      {/* What I Bring Section */}
      <section className="py-24 bg-white" data-id="element-536">
        <div className="max-w-7xl mx-auto px-6" data-id="element-537">
          <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-538">
            <h2 className="text-3xl md:text-4xl font-light text-slate-900 mb-6" data-id="element-539">
              What I Bring to{' '}
              <span className="font-normal text-blue-500" data-id="element-540">Your Team</span>
            </h2>
            <p className="text-lg text-slate-600 font-light leading-relaxed" data-id="element-541">
              I thrive in collaborative environments where design, product, and
              engineering work closely together. Here's how I contribute.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8" data-id="element-542">
            <ServiceFlipCard icon={Compass} title="Experience Strategy" description="Successful products require concrete goals, a plan, and tough decision-making. I take the time to understand your business goals and product vision, then map a course for creating a highly-valued product over time." highlightedText="tough decision-making" activities={['Stakeholder interviews', 'Discovery workshop', 'Vision workshop', 'Roadmapping workshop']} deliverables={['Vision prototype', 'Product success measures', 'Human-centered roadmap']} data-id="element-543" />

            <ServiceFlipCard icon={Users} title="User Research" description="Creating tools people will actually use requires talking to the people you hope will use them. I connect directly with your audience to understand their needs and envision new and improved solutions together." highlightedText="talking to the people" activities={['Exploratory user interviews', 'Co-design workshops', 'Concept evaluation', 'Usability testing']} deliverables={['Research insights and design recommendations', 'Personas and journey maps', '"Jobs to be done"']} data-id="element-544" />

            <ServiceFlipCard icon={Palette} title="UX Design" description="Design isn't about sparkles and pretty pictures, it's creative problem solving. For each user problem or goal, I generate a variety of possible solutions, then iteratively refine them down to one I'm confident is both useful and usable." highlightedText="creative problem solving" activities={['Ideation workshops', 'Sketching and prototyping', 'Interaction design', 'Visual design']} deliverables={['Interactive prototypes', 'User flows', 'Design systems', 'Dev-ready assets']} data-id="element-545" />

            <ServiceFlipCard icon={Award} title="Design Leadership" description="I lead by doing, not just directing. I believe the best design leaders are player-coaches who ship features alongside their teams while elevating everyone's craft through mentorship and collaboration." highlightedText="player-coaches" activities={['Creative workshopping', 'Team design critiques', 'Mentoring', 'Empathetic feedback']} deliverables={['Team goals + objectives', 'Design principles', 'Skills matrix', 'Team growth plans']} data-id="element-546" />
          </div>
        </div>
      </section>

      {/* Featured Work */}
      <section id="work" className="py-16 bg-slate-50" data-id="element-547">
        <div className="max-w-7xl mx-auto px-6" data-id="element-548">
          <div className="flex justify-between items-end mb-8" data-id="element-549">
            <div data-id="element-550">
              <h2 className="text-3xl md:text-4xl font-light text-slate-900 mb-4" data-id="element-551">
                Featured Projects
              </h2>
              <p className="text-slate-500 font-light" data-id="element-552">
                A selection of work from my career in digital health and
                enterprise software.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-553">
            <ProjectCard id="embr" title="Embr Wave 2" category="Wearable + Mobile" description="Reinventing the Embr Wave for menopause, delivering personalized features for fast hot flash relief." image="https://cdn.magicpatterns.com/uploads/htdDt1KjwZu8NrQ8AZSGUc/embr-hero.jpg" tags={['B2C', 'Wearable Device', 'Mobile App', 'Scale-up']} link="/work/embr" data-id="element-554" />
            <ProjectCard id="ginkgo" title="Ginkgo Biosecurity" category="Enterprise Platform" description="Transforming a national Covid testing program into an innovative biosecurity platform tracking pathogens globally." image="https://cdn.magicpatterns.com/uploads/hVpLMTsTnubs7Jy9HdF6u7/ginkgo.png" tags={['B2G', 'Enterprise Platform', 'Product Pivot', 'Scale-up']} link="/work/ginkgo" data-id="element-555" />
            <ProjectCard id="innermap" title="Innermap" category="Mobile App" description="Creating an empathic AI companion to help people explore and understand their emotions through interactive mapping." image="https://cdn.magicpatterns.com/uploads/aYUuTfkAwiNHT5CnXzMn6W/affi-laptop.png" tags={['B2B', 'B2C', 'Mobile App', 'SaaS', 'Start-up']} link="/work/innermap" data-id="element-556" />
            <ProjectCard id="root-beliefs" title="Root Beliefs" category="Mobile App" description="Bringing a psychotherapist's vision to life with a self-guided, multimedia app for mental health literacy." image="https://cdn.magicpatterns.com/uploads/daKjz8v49q51Hm5Jgi1e3M/rootbelief.png" tags={['B2C', 'Mobile App', 'Start-up']} comingSoon={true} data-id="element-557" />
          </div>
        </div>
      </section>

      {/* About Me & Values Section */}
      <section id="about" className="py-24 bg-white border-t border-slate-100" data-id="element-558">
        <div className="max-w-5xl mx-auto px-6" data-id="element-559">
          {/* Tab Navigation */}
          <div className="flex justify-center mb-12" data-id="element-560">
            <div className="relative inline-flex bg-slate-100 rounded-full p-1" data-id="element-561">
              {/* Animated Background Indicator */}
              <motion.div className="absolute top-1 bottom-1 bg-white rounded-full shadow-sm" initial={false} animate={{
              left: activeTab === 'love' ? '0.25rem' : activeTab === 'values' ? 'calc(33.333% + 0.125rem)' : 'calc(66.666% + 0.125rem)',
              width: 'calc(33.333% - 0.25rem)'
            }} transition={{
              type: 'spring',
              stiffness: 300,
              damping: 30
            }} data-id="element-562" />

              <button onClick={() => setActiveTab('love')} className={`relative z-10 px-6 py-3 rounded-full text-sm font-medium transition-colors ${activeTab === 'love' ? 'text-blue-600' : 'text-slate-600 hover:text-slate-900'}`} data-id="element-563">
                What I Love
              </button>
              <button onClick={() => setActiveTab('values')} className={`relative z-10 px-6 py-3 rounded-full text-sm font-medium transition-colors ${activeTab === 'values' ? 'text-blue-600' : 'text-slate-600 hover:text-slate-900'}`} data-id="element-564">
                My Values
              </button>
              <button onClick={() => setActiveTab('bio')} className={`relative z-10 px-6 py-3 rounded-full text-sm font-medium transition-colors ${activeTab === 'bio' ? 'text-blue-600' : 'text-slate-600 hover:text-slate-900'}`} data-id="element-565">
                About Me
              </button>
            </div>
          </div>

          {/* Tab Content */}
          <AnimatePresence mode="wait" data-id="element-566">
            {activeTab === 'love' && <motion.div key="love" initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} exit={{
            opacity: 0,
            y: -20
          }} transition={{
            duration: 0.3
          }} className="max-w-3xl mx-auto" data-id="element-567">
                <h2 className="text-3xl md:text-4xl font-light text-slate-900 mb-12 text-center" data-id="element-568">
                  What I love about product design [and in life]
                </h2>

                <div className="space-y-10" data-id="element-569">
                  <motion.div initial={{
                opacity: 0,
                x: -20
              }} animate={{
                opacity: 1,
                x: 0
              }} transition={{
                delay: 0.1
              }} data-id="element-570">
                    <h3 className="text-xl font-semibold text-blue-600 mb-4 flex items-center gap-3" data-id="element-571">
                      <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0" data-id="element-572">
                        <Brain className="text-blue-600" size={22} data-id="element-573" />
                      </div>
                      Learning new things
                    </h3>
                    <p className="text-slate-600 font-light leading-relaxed text-lg ml-15" data-id="element-574">
                      I love learning about what other people do for work; why
                      they do it, how they do it, and which parts are the most
                      challenging and the most fun.
                    </p>
                  </motion.div>

                  <motion.div initial={{
                opacity: 0,
                x: -20
              }} animate={{
                opacity: 1,
                x: 0
              }} transition={{
                delay: 0.2
              }} data-id="element-575">
                    <h3 className="text-xl font-semibold text-blue-600 mb-4 flex items-center gap-3" data-id="element-576">
                      <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0" data-id="element-577">
                        <Wrench className="text-blue-600" size={22} data-id="element-578" />
                      </div>
                      Getting my hands dirty
                    </h3>
                    <p className="text-slate-600 font-light leading-relaxed text-lg ml-15" data-id="element-579">
                      The process of rolling up my sleeves and making something
                      with my own two hands is incredibly gratifying.
                    </p>
                  </motion.div>

                  <motion.div initial={{
                opacity: 0,
                x: -20
              }} animate={{
                opacity: 1,
                x: 0
              }} transition={{
                delay: 0.3
              }} data-id="element-580">
                    <h3 className="text-xl font-semibold text-blue-600 mb-4 flex items-center gap-3" data-id="element-581">
                      <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0" data-id="element-582">
                        <Heart className="text-blue-600" size={22} data-id="element-583" />
                      </div>
                      Helping people
                    </h3>
                    <p className="text-slate-600 font-light leading-relaxed text-lg ml-15" data-id="element-584">
                      Knowing that my creations have helped people in the real
                      world, whether at work or in their lives, brings me
                      immense joy.
                    </p>
                  </motion.div>
                </div>
              </motion.div>}

            {activeTab === 'values' && <motion.div key="values" initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} exit={{
            opacity: 0,
            y: -20
          }} transition={{
            duration: 0.3
          }} className="max-w-4xl mx-auto" data-id="element-585">
                <div className="flex flex-col md:flex-row gap-12 items-start" data-id="element-586">
                  <motion.div initial={{
                opacity: 0,
                scale: 0.9
              }} animate={{
                opacity: 1,
                scale: 1
              }} transition={{
                delay: 0.2
              }} className="w-full md:w-2/5 flex-shrink-0" data-id="element-587">
                    <img src="https://cdn.magicpatterns.com/uploads/4XZnAfus5gycbJcmQQmhgx/ice.png" alt="Two dogs walking across a frozen lake on a sunny winter day" className="w-full rounded-full shadow-xl object-cover aspect-square" data-id="element-588" />
                  </motion.div>

                  <motion.div initial={{
                opacity: 0,
                x: 20
              }} animate={{
                opacity: 1,
                x: 0
              }} transition={{
                delay: 0.3
              }} className="flex-1 space-y-10" data-id="element-589">
                    <h2 className="text-3xl md:text-4xl font-light text-slate-900" data-id="element-590">
                      My Values
                    </h2>

                    <div className="space-y-8" data-id="element-591">
                      <div data-id="element-592">
                        <h3 className="text-xl font-medium text-slate-900 mb-3" data-id="element-593">
                          Be curious.
                        </h3>
                        <p className="text-slate-600 font-light leading-relaxed text-lg" data-id="element-594">
                          Ask smart questions, not easy ones. Stay open to new
                          ideas, not just those that validate your own.
                        </p>
                      </div>

                      <div data-id="element-595">
                        <h3 className="text-xl font-medium text-slate-900 mb-3" data-id="element-596">
                          Listen deeply.
                        </h3>
                        <p className="text-slate-600 font-light leading-relaxed text-lg" data-id="element-597">
                          Listen to what people don't say as much as to what
                          they do say. Embrace the awkward silence, good stuff
                          is learned afterwards.
                        </p>
                      </div>

                      <div data-id="element-598">
                        <h3 className="text-xl font-medium text-slate-900 mb-3" data-id="element-599">
                          Work together.
                        </h3>
                        <p className="text-slate-600 font-light leading-relaxed text-lg" data-id="element-600">
                          Approach the work collaboratively, not competitively.
                          The buddy system works well and is way more fun.
                        </p>
                      </div>

                      <div data-id="element-601">
                        <h3 className="text-xl font-medium text-slate-900 mb-3" data-id="element-602">
                          Go outside.
                        </h3>
                        <p className="text-slate-600 font-light leading-relaxed text-lg" data-id="element-603">
                          The best ideas aren't inspired by staring at a screen.
                          Get some air, play with the dogs, look up at the
                          trees.
                        </p>
                      </div>
                    </div>
                  </motion.div>
                </div>
              </motion.div>}

            {activeTab === 'bio' && <motion.div key="bio" initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} exit={{
            opacity: 0,
            y: -20
          }} transition={{
            duration: 0.3
          }} className="max-w-4xl mx-auto" data-id="element-604">
                <div className="flex flex-col md:flex-row gap-12 items-center" data-id="element-605">
                  <motion.div initial={{
                opacity: 0,
                scale: 0.9
              }} animate={{
                opacity: 1,
                scale: 1
              }} transition={{
                delay: 0.2
              }} className="w-full md:w-2/5 flex-shrink-0" data-id="element-606">
                    <img src="https://cdn.magicpatterns.com/uploads/fYF4Q9fHEqXh3yNCgkFyFF/working.png" alt="Working on laptop" className="w-full rounded-full shadow-xl object-cover aspect-square" data-id="element-607" />
                  </motion.div>

                  <motion.div initial={{
                opacity: 0,
                x: 20
              }} animate={{
                opacity: 1,
                x: 0
              }} transition={{
                delay: 0.3
              }} className="flex-1 space-y-6" data-id="element-608">
                    <h2 className="text-3xl md:text-4xl font-light text-slate-900" data-id="element-609">
                      About Me
                    </h2>
                    <div className="space-y-4 text-slate-600 font-light leading-relaxed text-lg" data-id="element-610">
                      <p data-id="element-611">
                        I'm a UX designer and researcher who believes that
                        technology, made with caring hands, can make a positive
                        difference in the lives of all.
                      </p>
                      <p data-id="element-612">
                        Throughout my career in digital health and enterprise
                        software, I've had the privilege of creating products
                        that genuinely help people—whether in their health,
                        their jobs, or their everyday lives.
                      </p>
                      <p data-id="element-613">
                        Outside of work, you'll find me crafting something in
                        the kitchen, playing with the dogs, or digging holes in
                        the yard for new plants.
                      </p>
                    </div>
                  </motion.div>
                </div>
              </motion.div>}
          </AnimatePresence>
        </div>
      </section>

      <Testimonials data-id="element-614" />
      <Footer data-id="element-615" />
    </div>;
}